import React, { useState } from 'react';
import connectIcon from '../../assets/images/connect.png';
import sideImage from '../../assets/images/contact-us-right.png';
import { validatePhoneNumber, validateEmail } from '../../utils/validation';

function ContactSection() {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    about: '',
    aboutOther: '',
    subject: '',
    message: '',
  });

  const [errors, setErrors] = useState({});
  const [result, setResult] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    let filteredValue = value;

    if (name === "fullName") {
      // Allow only letters and spaces
      filteredValue = value.replace(/[^a-zA-Z\s]/g, "");
    }

    if (name === "phone") {
      // Allow only digits and limit to 10
      filteredValue = value.replace(/[^0-9]/g, "").slice(0, 10);
    }

    setFormData((prev) => ({
      ...prev,
      [name]: filteredValue,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    // ✅ Full Name validation
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full Name is required.";
    } else if (!/^[a-zA-Z\s]+$/.test(formData.fullName)) {
      newErrors.fullName = "Name should contain only letters.";
    }

    // ✅ Phone Number validation
    if (!formData.phone.trim()) {
      newErrors.phone = "Phone Number is required.";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Phone Number must be 10 digits.";
    }

    // ✅ Email validation (using your helper)
    const emailError = validateEmail(formData.email);
    if (emailError) newErrors.email = emailError;

    // ✅ About section validation
    if (!formData.about) {
      newErrors.about = "Please select an option.";
    } else if (formData.about === "other" && !formData.aboutOther.trim()) {
      newErrors.aboutOther = "Please specify your option.";
    }

    // ✅ Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required.";
    }

    // ✅ Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setResult('Sending...');

    const form = new FormData();
    form.append('access_key', 'dd008842-7240-4294-b550-6b009b733248'); // 🔑 Replace this with your real key
    form.append('name', formData.fullName);
    form.append('phone', formData.phone);
    form.append('email', formData.email);
    form.append(
      'about',
      formData.about === 'other' ? `Other: ${formData.aboutOther}` : formData.about
    );
    form.append('subject', formData.subject);
    form.append('message', formData.message);

    const response = await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      body: form,
    });

    const data = await response.json();
    if (data.success) {
      setResult('Form Submitted Successfully!');
      setFormData({
        fullName: '',
        phone: '',
        email: '',
        about: '',
        aboutOther: '',
        subject: '',
        message: '',
      });
    } else {
      console.log('Error:', data);
      setResult(data.message || 'Submission failed');
    }
  };

  return (
    <section className="w-full min-h-[900px] flex items-center justify-center px-4 py-8">
      <div className="w-full max-w-[1381px] mx-auto flex flex-col lg:flex-row items-center">
        <div className="w-full max-w-[716px] bg-white rounded-[18.91px] border p-6 flex flex-col items-center">
          <h3 className="text-center font-dongle text-[45px] sm:text-[52px] md:text-[61.97px] leading-[43px] font-normal text-black">
            Have Questions? Let’s Connect!
            <span>
              <img src={connectIcon} alt="" className="inline-block w-8 h-8 object-contain ml-2" />
            </span>
          </h3>

          <form onSubmit={handleSubmit} className="w-full max-w-[620px] mt-6 flex flex-col gap-5">
            {/* Name + Phone */}
            <div className="flex flex-col md:flex-row gap-4">
              <div className="w-full">
                <input
                  type="text"
                  name="fullName"
                  placeholder="Full Name"
                  value={formData.fullName}
                  onChange={handleChange}
                  className="w-full h-[54px] px-3 border rounded-md"
                />
                {errors.fullName && <p className="text-red-500 text-sm mt-1">{errors.fullName}</p>}
              </div>
              <div className="w-full">
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleChange}
                  className="w-full h-[54px] px-3 border rounded-md"
                />
                {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
              </div>
            </div>

            {/* Email */}
            <div>
              <input
                type="email"
                name="email"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
                className="w-full h-[54px] px-3 border rounded-md"
              />
              {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
            </div>

            {/* What is it about? */}
            <div className="border px-4 py-3 rounded-md">
              <p className="mb-2 text-black font-medium">What Is It About?</p>
              <div className="flex flex-wrap gap-4">
                {['resident', 'delivery', 'commercial'].map((option) => (
                  <label key={option} className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="about"
                      value={option}
                      checked={formData.about === option}
                      onChange={handleChange}
                    />
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </label>
                ))}
                <div className="flex flex-wrap items-center gap-2">
                  <label className="flex items-center gap-1">
                    <input
                      type="radio"
                      name="about"
                      value="other"
                      checked={formData.about === 'other'}
                      onChange={handleChange}
                    />
                    Other
                  </label>
                  <input
                    type="text"
                    name="aboutOther"
                    value={formData.aboutOther}
                    onChange={handleChange}
                    placeholder="Specify"
                    className="border-b border-gray-400 outline-none flex-1 min-w-[100px] sm:min-w-[200px]"
                  />
                </div>
              </div>
              {errors.about && <p className="text-red-500 text-sm mt-1">{errors.about}</p>}
              {errors.aboutOther && <p className="text-red-500 text-sm mt-1">{errors.aboutOther}</p>}
            </div>

            {/* Subject + Message */}
            <div className="border px-4 py-3 rounded-md flex flex-col gap-2">
              <input
                type="text"
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                placeholder="Subject"
                className="w-full h-[40px] px-2 border rounded"
              />
              {errors.subject && <p className="text-red-500 text-sm mt-1">{errors.subject}</p>}
              <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder="Message"
                className="w-full h-[70px] px-2 pt-2 border rounded resize-none"
              />
              {errors.message && <p className="text-red-500 text-sm mt-1">{errors.message}</p>}
            </div>

            {/* Submit */}
            <div className="flex justify-center">
              <button
                type="submit"
                className="w-[450px] h-[50px] rounded-[10px] px-[10.56px] py-[12.67px] bg-[#2699FF] text-white text-lg uppercase hover:bg-blue-600 transition"
              >
                Contact Us Today
              </button>
            </div>

            {result && (
              <p className="text-center mt-2 text-green-600 font-semibold">{result}</p>
            )}
          </form>
        </div>

        {/* Right Image */}
        <div className="flex justify-center items-center w-full max-w-[633.69px] mt-10 lg:mt-0">
          <img
            src={sideImage}
            alt="Contact visual"
            className="w-full max-w-[633.69px] h-auto object-contain"
          />
        </div>
      </div>
    </section>
  );
}

export default ContactSection;